<template>
  <div id="productFeaturesSection" :class="['main-area', { 'without-category': !isCategorySelected }]">
    <PageTop title="Ürün Özellikleri" />
    <div class="info-text" v-if="!isCategorySelected">
      <Icons name="InfoCircle" color="#7382a0" />
      <p>
        Ürün özelliklerini girebilmek için önce <span>kategori</span> seçimi yapmalısınız.
      </p>
    </div>
    <div v-else>
      <div class="dropdown-list-wrapper d-flex flex-column">
        <div class="required-list" v-if="mandatoryAttributes.length">
          <div class="required-list-info d-flex flex-row">
            <p>{{ dropdownList.requiredField.title }}</p>
            <span class="required-badge">ZORUNLU ÖZELLİKLER*</span>
          </div>
          <div class="input-area">
            <DropdownSelectable
              v-for="(item, index) in mandatoryAttributes"
              :key="index"
              :dropItems="item.currentDropItems"
              :isSearchBackend="item.searchable"
              @selectedRadioItem="handleSelectedRadioItem($event, item)"
              :label="`${item.attributeName}*`"
              notFoundText="Aradığınız değer bulunamadı.<br> Farklı değerlerle tekrar aramayı deneyebilirsiniz."
              :shouldShowEmptyIcon="true"
              isComboBox
              customInputLabel="Yeni Değer Ekle"
              :isCustomValue="item.isCustomValue"
              :singleItemTextLength="singleItemTextLength"
              :errorText="item.errorText ?? null "
              :searchText="item.attributeName + ' Ara'"
              :searchDelay="item.searchable ? 0 : 500"
              @changeSearchValue="handleSearch($event, item)"
            />
          </div>
        </div>
        <div class="optional-list" v-if="optionalAttributes.length">
          <p>{{ dropdownList.optionalField.title }}</p>
          <div class="input-area">
            <DropdownSelectable
              v-for="(item, index) in optionalAttributes"
              :key="index"
              :dropItems="item.currentDropItems"
              @selectedRadioItem="handleSelectedRadioItem($event, item)"
              :label="`${item.attributeName} (Opsiyonel)`"
              notFoundText="Aradığınız değer bulunamadı.<br> Farklı değerlerle tekrar aramayı deneyebilirsiniz."
              :shouldShowEmptyIcon="true"
              isComboBox
              customInputLabel="Yeni Değer Ekle"
              :isCustomValue="item.isCustomValue"
              :singleItemTextLength="singleItemTextLength"
              :searchText="item.attributeName + ' Ara'"
              :searchDelay="item.searchable ? 0 : 500"
              @changeSearchValue="handleSearch($event, item)"
              :isSearchBackend="item.searchable"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import PageTop from '@/components/Page/PageTop.vue'
import { DropdownSelectable, Icons } from "@n11/so-ui-components"
import store from '@/store';

const props = defineProps({
  isCategorySelected: {
    type: Boolean,
    required: true,
    default: false,
  },
  categoryAttributes: {
    type: Array,
    required: true,
    default: () => [],
  },
})

const processedAttributes = ref([]);

watch(() => props.categoryAttributes, (newAttributes) => {
  processedAttributes.value = newAttributes.map(attr => ({
    ...attr,
    currentDropItems: mapAttributeValues(attr.attributeValues)
  }));
}, {
  immediate: true,
  deep: true
});

const mapAttributeValues = (values) => {
  if (!values) return [];
  return values.map(item => ({ id: item.id || item.valueId, name: item.value }));
};

const mandatoryAttributes = computed(() =>
  processedAttributes.value.filter(item =>
    !item.isVariant && !item.isSlicer && item.isMandatory && item.attributeId !== 1
  )
);

const optionalAttributes = computed(() =>
  processedAttributes.value.filter(item =>
    !item.isVariant && !item.isSlicer && !item.isMandatory && item.attributeId !== 1
  )
);

const selections = ref([])
const singleItemTextLength = ref(80)

// const filterAttributes = (isMandatory) => {
//   return props.categoryAttributes.filter(item =>
//     !item.isVariant && !item.isSlicer && item.isMandatory === isMandatory && item.attributeId !== 1
//   )
// }

const handleSearch = async (newValue, item) => {
  try {
    if (!newValue || newValue.trim() === '') {
      item.currentDropItems = mapAttributeValues(item.attributeValues);
      return;
    }
    const searchResult = await store.dispatch("categories/searchAttributeValue", { 
      attributeId: item.attributeId, 
      categoryId: item.categoryId, 
      value: newValue 
    });
    const searchItems = searchResult.data.valueContents?.map(({value, valueId}) => ({id: valueId, name: value}));
    item.currentDropItems = searchItems;
  }
  catch(err) {
    console.warn(err);
    item.currentDropItems = mapAttributeValues(item.attributeValues);
  }
}

const dropdownList = {
  requiredField: {
    title: "Ürünün satışa açılması için öncelikle aşağıdaki zorunlu alanları seçmelisiniz.",
  },
  optionalField: {
    title: "Ürünün görünürlüğünü ve satışını artırmak için diğer özellikleri aşağıdan seçebilirsiniz.",
  },
}

const handleSelectedRadioItem = (e, item) => {
  const selectedItem = {
    feature: item,
    selection: e.name,
  }
  const index = selections.value.findIndex((s) => s?.feature.attributeId === item.attributeId)
  item.errorText = null
  if (index === -1)
    selections.value.push(selectedItem)
  else
    selections.value[index] = selectedItem
}

const getProductFeatures = async () => {
  const allDataIsValid = mandatoryAttributes.value.every(mandatory =>
    selections.value.some(selection => selection?.feature.attributeId === mandatory.attributeId)
  )

  mandatoryAttributes.value.forEach(mandatory => {
    const isValid = selections.value.some(selection =>
      selection?.feature.attributeId === mandatory.attributeId
    )

    if (!isValid) {
      mandatory.errorText = `${mandatory.attributeName} zorunludur.`
    } else {
      mandatory.errorText = ""
    }
  })

  return {
    data: {
      features: selections.value
    },
    isValid: allDataIsValid,
  }
}

watch(
  () => props.categoryAttributes,
  () => {
    selections.value = []
  }
)

defineExpose({
  getProductFeatures
})
</script>


<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.main-area {
  width: 100%;
  display: flex;
  gap: 16px;
  padding: 24px;
  border: 1px solid #D3D6DF;
  border-radius: 8px;

  &.without-category {
    background: $colorD05 !important;
  }

  .input-area {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    & > div {
      height: max-content;
    }

    .input-with-title {
      gap: 12px;
      font-size: 14px;
      line-height: 18px;
      font-weight: 600;
      color: $colorN80;
    }
  }

  .info-text {
    display: flex;
    align-items: center;
    gap: 8px;
    color: $colorD50;
    font-size: 13px;
    line-height: 18px;

    span {
      font-weight: 600;
    }
  }

  .dropdown-list-wrapper {
    gap: 16px;

    .required-list, .optional-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .required-list {
      padding-bottom: 24px;
      border-bottom: 0.5px solid $colorD30;
    }

    .required-list-info {
      gap: 8px;

      span {
        border-radius: 3px;
        padding: 1px 8px;
        background: $colorN00;
        color: $colorM50;
        border: 1px solid $colorM50;
        font-size: 10px;
        line-height: 13.62px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 800;
      }
    }

    p {
      font-size: 14px;
      line-height: 18px;
      font-weight: 600;
      color: $colorN80
    }
  }

}

</style>
